import * as React from 'react'

import {
    Accordion as MuiAccordion,
    AccordionDetails as MuiAccordionDetails,
    AccordionSummary as MuiAccordionSummary,
    useTheme,
} from '@mui/material'
import Stack from '@mui/material/Stack'
import { styled } from '@mui/material/styles'

import { I18n, useTranslation } from 'components/hooks/use-translation'
import useWindowSize from 'components/hooks/use-window-size'
import ModuleItem from 'components/atomic/organisms/syllabus/components/module-item'
import IconButton from 'components/atomic/atoms/icon-button'
import Chip from 'components/atomic/atoms/chip'
import Text from 'components/atomic/atoms/text'
import Icon from 'components/atomic/atoms/icon'
import { Track, PathProgress } from 'types/types'

const Bullet = styled('div')`
    width: 1rem;
    height: 1rem;
    border-radius: 50%;
    flex-shrink: 0;
    background: ${(props) => props.color || 'inherit'};
`

const Accordion = styled(MuiAccordion)`
    border-bottom: ${({ theme }) => `1px solid ${theme.palette.divider}`};

    &:before {
        display: none;
    }

    &.Mui-expanded {
        border: none;
    }
`

const AccordionSummary = styled(MuiAccordionSummary)`
    padding: 0;
    min-height: 50px !important;
    & .MuiAccordionSummary-content {
        align-items: center;
        justify-content: space-between;
    }
`

const AccordionDetails = styled(MuiAccordionDetails)`
    padding: ${({ theme }) => `0 0 ${theme.spacing(2)} ${theme.spacing(2)}`};
    margin-left: calc(0.5rem - 1px);
    row-gap: 1rem;
    display: flex;
    flex-direction: column;
    border-left: ${(props) => `1px solid ${props.color}`};
`

interface TrackItemProps {
    pathId: string
    pathProgress?: PathProgress
    track: Track
    color: string
}

const TrackItem = ({ pathId, track, pathProgress, color }: TrackItemProps) => {
    const [expanded, setExpanded] = React.useState(false)

    const { width } = useWindowSize()
    const { t }: I18n = useTranslation()
    const { palette, breakpoints } = useTheme()

    const isMobile = React.useMemo(() => width <= breakpoints.values.md, [width, breakpoints])

    const trackProgress = React.useMemo(
        () => pathProgress?.tracks.find((t) => t._id === track._id),
        [pathProgress, track],
    )

    return (
        <Accordion
            expanded={expanded}
            onChange={() => setExpanded((prev) => !prev)}
            key={track._id}
            TransitionProps={{ unmountOnExit: true }}
            disableGutters
            elevation={0}
            square>
            <AccordionSummary
                expandIcon={
                    <IconButton
                        aria-label="expand section"
                        color={expanded ? 'primary' : 'inherit'}
                        icon={'expandMore'}
                    />
                }
                aria-controls={`panel-${track._id}-content`}
                id={`panel-${track._id}-header`}>
                <Stack direction="row" alignItems="center" spacing={1}>
                    <Bullet color={color} />

                    <Text variant="h6" color={expanded ? color : 'inherit'}>
                        {track.title}
                    </Text>

                    {trackProgress?.completed && !isMobile && (
                        <Chip
                            color="primary"
                            size="small"
                            variant="filled"
                            label={`${t('path:status.COMPLETED')}`}
                            rounded={false}
                        />
                    )}

                    {trackProgress?.started && !trackProgress?.completed && !isMobile && (
                        <Chip
                            sx={{ background: palette.grey[800], marginRight: '1rem', minWidth: '5.5rem' }}
                            size="small"
                            variant="filled"
                            label={`${t('path:status.IN_PROGRESS')}`}
                            rounded={false}
                        />
                    )}
                </Stack>

                <Stack mr="1rem" direction="row" alignItems="center" spacing={1}>
                    {trackProgress?.completed && isMobile && <Icon name="taskAliIcon" />}

                    {!isMobile && (
                        <Chip
                            size="small"
                            variant="filled"
                            label={`${track.modules.length} ${t('common:pathBoard.chapters')}`}
                            sx={{ background: palette.grey[800], minWidth: '5.5rem' }}
                            rounded={false}
                        />
                    )}
                </Stack>
            </AccordionSummary>

            <AccordionDetails color={color}>
                {track.modules.map((moduleItem, index) => (
                    <ModuleItem
                        key={moduleItem._id}
                        index={index}
                        rootId={pathId}
                        rootType="path"
                        module={moduleItem}
                        moduleProgress={pathProgress?.modules.find((item) => item._id === moduleItem._id)}
                        resourceIds={moduleItem.resources}
                        resourceProgress={pathProgress?.resources}
                    />
                ))}
            </AccordionDetails>
        </Accordion>
    )
}

export default TrackItem
